import React, { useRef, useState, useEffect, useCallback, memo, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Vector3, Color, Group, DoubleSide, Raycaster, Euler, Mesh } from 'three';
import { useLocalSessionId, useAppMessage, useMeetingState, useParticipantIds, useDaily } from '@daily-co/daily-react';
import { DailyEventObjectAppMessage, DailyEventObjectParticipant, DailyEventObjectParticipantLeft } from '@daily-co/daily-js';
import { createAppMessage } from '../../../utils/call';
import { SetUserTableValueWorldMessage, SetUserPositionAndRotationWorldMessage } from '../../../models/Types';
import _ from 'lodash';
import { useTableCamera, TableCameraContextValue } from './UseTableCamera';
import { useCameraControls, CameraControlsContextValue } from '../../react/context/CameraControlsCallbackContext';
import Seat from './Seat';
import { ThreeEvent, useThree } from '@react-three/fiber';

interface CircularVirtualTableProps {
  tableIndex: number;
  radius: number;
  numSeats: number;
  position: Vector3;
  onSeatUpdate: (tableIndex: number, seatIndex: number, userId: string) => void;
}

// const CircularVirtualTable: React.FC<CircularVirtualTableProps> = memo(({ tableIndex, radius, numSeats, position, onSeatUpdate }) => {
const CircularVirtualTable = memo(forwardRef((props: CircularVirtualTableProps, ref) => {
  const [noop, triggerUpdate] = useState(0);
  const { tableIndex, radius, numSeats, position, onSeatUpdate } = props;
  // console.log('cvt rendered', tableIndex, radius, numSeats, position);
  // const [seats, setSeats] = useState<string[]>(Array.from({ length: numSeats }, () => ''));
  const seats = useRef<string[]>(Array.from({ length: numSeats }, () => ''));
  const localParticipantId = useLocalSessionId();
  const groupRef = useRef<Group>(null);
  const localSeat = useRef<number>(-1);
  const { invalidate } = useThree();

  const seatPositions = Array.from({ length: numSeats }).map((_, index) => {
    const angle = (index / numSeats) * Math.PI * 2;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    return new Vector3(x, 0, z);
  });

  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const hasDoneInitialAssign = useRef(false);

  // useEffect(() => {
  //   waitAndAssignSeat();
  // }, [localParticipantId]);

  // const waitAndAssignSeat = async () => {
  //   await new Promise(resolve => setTimeout(resolve, 3000));
  //   if (localParticipantId !== '' && tableIndex === 0 && remoteParticipantIds.length === 0 && !hasDoneInitialAssign.current) { // Need to do this once you have gotten get-initial-user messages from all remote participants
  //     console.log('assigning seat after wait', remoteParticipantIds.length);
  //     assignSeat(localParticipantId);
  //   }
  // }

  const assignSeat = (userId: string) => {
    hasDoneInitialAssign.current = true;
    const index = seats.current.findIndex(seat => seat === '');
    if (index === -1) return;
    // handleSeatClick(index, seatPositions[index]);
  }

  useImperativeHandle(ref, () => ({
    assignSeat,
  }));

  const userTableMsgs = useRef(0);

  useAppMessage({
    onAppMessage: (message: DailyEventObjectAppMessage) => {
      const appMessage = createAppMessage(message);
      if (appMessage.message.messageType !== 'world' || appMessage.message.worldMessageType !== 'set-user-table-value') return;
      const userTableMessage = appMessage.message as SetUserTableValueWorldMessage;
      userTableMsgs.current += 1;
      if (userTableMessage.tablePosition.position.tableIndex === tableIndex) {
        const seatIndex = userTableMessage.tablePosition.position.seatIndex;
        const userId = userTableMessage.tablePosition.userId;
        // seats.current[seatIndex] = userId;
        updateSeats(seatIndex, userId);
        onSeatUpdate(tableIndex, seatIndex, userId);
      }
      // console.log(`${localParticipantId.substr(0,5)} got user table msg: (${userTableMessage.tablePosition.position.tableIndex},${userTableMessage.tablePosition.position.seatIndex}) from: ${userTableMessage.tablePosition.userId.substr(0,5)} msgCount: ${userTableMsgs.current}, remoteIds: ${remoteParticipantIds.length}`);
      if (userTableMsgs.current >= remoteParticipantIds.length && tableIndex === 0 && !hasDoneInitialAssign.current) {
        // console.log('assigning seat after getting remote table assign msgs');
        assignSeat(localParticipantId);
      }
    },
  });

  useParticipantIds({
    onParticipantLeft: (p: DailyEventObjectParticipantLeft) => {
      const participantId = p.participant.session_id;
      const seatForParticipant = seats.current.findIndex(seat => seat === participantId);
      if (seatForParticipant !== -1) updateSeats(seatForParticipant, ''); //seats.current[seatForParticipant] = '';
    },
  });


  const isLocalParticipantSeatedInCurrentTable = () => localSeat.current !== -1;

  const { moveCameraToSeat } = useTableCamera() as TableCameraContextValue;
  const hudCamControls = useCameraControls();

  useEffect(() => {
    if (!hudCamControls) return;

    const handleMoveForward = (isActive: boolean) => {
      if (!isLocalParticipantSeatedInCurrentTable() || !isActive) return;
      vacateLocalSeat();
      };
    const handleMoveBackward = (isActive: boolean) => {
      if (!isLocalParticipantSeatedInCurrentTable() || !isActive) return;
      vacateLocalSeat();
      };

    const moveForwardSubscription = hudCamControls.onMoveForward(handleMoveForward);
    const moveBackardSubscription = hudCamControls.onMoveBackward(handleMoveBackward);

    return () => {
      moveForwardSubscription();
      moveBackardSubscription();
    };
    
  }, [hudCamControls]);

  const vacateLocalSeat = () => {
    const seatToVacate = _.clone(localSeat.current);
    // seats.current[seatToVacate] = '';
    // localSeat.current = -1;
    updateLocalSeat(-1);
    updateSeats(seatToVacate, '');
    onSeatUpdate(tableIndex, -1, localParticipantId);
  };


  const handleKeyPressed = (event: KeyboardEvent) => {
    const key = _.toLower(event.key);
    if (key === 'w' || key === 's' || key === 'a' || key === 'd' || key === 'arrowup' || key === 'arrowdown') {
      vacateLocalSeat();
      // console.log('vacated local seat from key press');
    }
  };

  const updateLocalSeat = (seatIndex: number) => {
    localSeat.current = seatIndex;
    if (seatIndex !== -1) window.addEventListener('keydown', handleKeyPressed);
    else window.removeEventListener('keydown', handleKeyPressed); // maybe hae an empty effect that does this on return
  }

  const updateSeats = (seatIndex: number, userId: string) => {
    seats.current[seatIndex] = userId;
    triggerUpdate(prev => prev + 1);
  }

  const handleSeatClick = (index: number, seatPosition: Vector3, event?: ThreeEvent<PointerEvent>) => {
    // console.log('handleSeatClick', index, seatPosition);
    const shouldMove = seats.current[index] === '' && (event === undefined || event.distance <= 10);
    if (shouldMove) {
      moveCameraToSeat(seatPosition, position);
      if (localSeat.current !== -1) vacateLocalSeat();
      // seats.current[index] = localParticipantId;
      // localSeat.current = index;
      updateLocalSeat(index);
      updateSeats(index, localParticipantId);
      // console.log('handleSeatClick updates...', localSeat.current, seats.current[index]);
      onSeatUpdate(tableIndex, index, localParticipantId);
      invalidate();
    }
  };

  return (
    <>
      <group position={position} ref={groupRef}>
        {seatPositions.map((seatPosition, index) => (
          <Seat key={index} position={seatPosition} seatedUserId={seats.current[index]} onClick={(event?) => handleSeatClick(index, seatPosition, event)} />
        ))}
      </group>
    </>
  );
}));

export default CircularVirtualTable;
